import { yupResolver } from '@hookform/resolvers/yup';
import cloneDeep from 'lodash/cloneDeep';
import { useStateMachine } from 'little-state-machine';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import useSelector from '@redux/typedHooks';
import { useServerErrors } from '@hooks/useServerErrors';
import { getPromotionDataFromSessionStorage } from '../../../../hooks/useTrackPromotion';
import { useTrackProductsCart } from '../../../../hooks/useTrackProductItem';
import { useProduct } from '../../BecomeAMember.utils';
import {
  didUseVippsMembershipRegistrationMethod,
  getVippsMembershipRegistrationMethodTrackingPayload,
} from '../../components/VippsModal/VippsModal.utils';
import { createServerFieldMappings } from '../../lib/createServerFieldMappings';
import { updateAction } from '../../lib/updateAction';
import { MemberSchema } from '../../schemas/MemberSchema';
import { useTrackingStep2 } from '../../utils/useTracking';
import {
  type AddonFormMember,
  type AddonsFormValues,
  getAddonProductSchema,
  getAddonsData,
  householdMemberAmount,
  householdMemberIndex,
} from './Addons.utils';

interface AddonsFormLogicOptions {
  goToStep?: (step: number) => void;
}

export const useAddonsFormLogic = ({ goToStep }: AddonsFormLogicOptions) => {
  const serverErrors = useSelector((state) => state.order.validation?.validationResults);
  const [numberOfMembers, setNumberOfMembers] = useState(1);
  const registrationMethod = getVippsMembershipRegistrationMethodTrackingPayload(
    didUseVippsMembershipRegistrationMethod(),
  ).checkout_registration_method;
  const campaignCode = useSelector((state) => state.becomeAMember?.data?.campaignCodeDetails?.campaignCode);
  const { trackAddToCart, trackRemoveFromCart } = useTrackProductsCart(registrationMethod, campaignCode);
  const { product } = useProduct();
  const previousCartRef = useRef<{ product?: typeof product; quantity: number } | null>(null);
  const {
    actions,
    state: { membershipForm, membersForm },
  } = useStateMachine({ updateAction });
  const [isHouseholdMemberSelected, setIsHouseholdMemberSelected] = useState(
    Number(membershipForm?.product?.amount) > 1 || !!membersForm.otherMembers?.[householdMemberIndex],
  );

  const membershipProduct = useSelector(
    (state) => state.becomeAMember.data.products[membershipForm?.product?.productId],
  );
  const supportsHouseholdMember =
    !!membershipProduct?.isHouseholdProduct && !!membershipProduct.productAppPrice?.[householdMemberAmount];
  const householdMemberPrice =
    (membershipProduct?.productAppPrice?.[householdMemberAmount]?.itemTotalPrice || 0) -
    (membershipProduct?.productAppPrice?.[1]?.itemTotalPrice || 0);
  const availableAddonProducts = membershipProduct?.availableAddOnProducts?.products;
  const addonProductsDescription = membershipProduct?.availableAddOnProducts?.description;

  const getUpdatedMembershipForm = (hasHouseholdMember: boolean) =>
    ({
      ...membershipForm,
      product: membershipForm.product
        ? {
            ...membershipForm.product,
            amount: hasHouseholdMember ? householdMemberAmount : 1,
          }
        : membershipForm.product,
    }) as typeof membershipForm;

  const getUpdatedMembersForm = (data: AddonsFormValues, hasHouseholdMember: boolean) => ({
    ...membersForm,
    otherMembers: hasHouseholdMember && data.otherMembers?.[householdMemberIndex] ? [data.otherMembers[0]] : [],
  });

  const addonsFormSchema = useMemo(() => {
    const addonProductShape =
      availableAddonProducts?.reduce((acc: Record<string, yup.AnySchema>, addonProduct) => {
        acc[addonProduct.productNumber] = getAddonProductSchema(addonProduct);
        return acc;
      }, {}) || {};
    return yup.object().shape({
      ...addonProductShape,
      otherMembers: isHouseholdMemberSelected
        ? yup.array().of(MemberSchema).min(1).required()
        : yup.array().notRequired(),
    });
  }, [availableAddonProducts, isHouseholdMemberSelected]);

  const form = useForm<any>({ resolver: yupResolver(addonsFormSchema), mode: 'onChange' });
  const {
    clearErrors,
    setError,
    setValue,
    unregister,
    watch,
    formState: { errors },
  } = form;

  const persistForm = (data: AddonsFormValues) => {
    const memberAmount = isHouseholdMemberSelected ? householdMemberAmount : 1;
    actions.updateAction({ formName: 'membershipForm', data: getUpdatedMembershipForm(isHouseholdMemberSelected) });
    actions.updateAction({ formName: 'membersForm', data: getUpdatedMembersForm(data, isHouseholdMemberSelected) });
    actions.updateAction({ formName: 'addonsForm', data: cloneDeep(getAddonsData(data, memberAmount)) });
  };

  useEffect(() => {
    if (membershipForm && !membershipForm.product && goToStep) goToStep(1);
  }, [membershipForm, goToStep]);

  useEffect(() => {
    setNumberOfMembers(isHouseholdMemberSelected ? householdMemberAmount : 1);
  }, [isHouseholdMemberSelected]);

  const serverFieldMap = useMemo(
    () => createServerFieldMappings(isHouseholdMemberSelected ? householdMemberAmount : 1).form2,
    [isHouseholdMemberSelected],
  );
  useServerErrors(serverErrors, serverFieldMap, setError);

  useTrackingStep2(membershipForm);

  const addonForm = watch();
  const householdMember = watch(`otherMembers.${householdMemberIndex}`);
  const storedHouseholdMember = membersForm.otherMembers?.[householdMemberIndex];
  const selectedHouseholdMember = householdMember || storedHouseholdMember;
  const members: AddonFormMember[] = [
    membersForm.mainMember,
    ...(isHouseholdMemberSelected ? [selectedHouseholdMember || {}] : []),
  ];

  const handleHouseholdMemberToggle = () => {
    const nextValue = !isHouseholdMemberSelected;
    setIsHouseholdMemberSelected(nextValue);
    setNumberOfMembers(nextValue ? householdMemberAmount : 1);
    actions.updateAction({ formName: 'membershipForm', data: getUpdatedMembershipForm(nextValue) });
    if (!nextValue) {
      unregister(`otherMembers.${householdMemberIndex}`);
      setValue('otherMembers', []);
      clearErrors('otherMembers');
    } else if (storedHouseholdMember) {
      setValue(`otherMembers.${householdMemberIndex}`, storedHouseholdMember);
    }
  };

  const products = {
    mainProduct: getUpdatedMembershipForm(isHouseholdMemberSelected).product,
    addOnProducts: Object.entries(addonForm as Record<string, { members?: string[] }>)
      .filter((entry) => Array.isArray(entry[1]?.members) && entry[1].members.length > 0)
      .map(([productId, { members: selectedMembers }]) => ({
        productId,
        amount: (selectedMembers?.length || 0).toString(),
      })),
  };
  const mainMemberServerMessages = Object.values((errors.mainMember || {}) as Record<string, { message?: string }>)
    .map((error) => error?.message)
    .filter(Boolean);
  const householdMemberErrors = errors.otherMembers?.[householdMemberIndex];
  const householdMemberBirthDateErrors = householdMemberErrors?.birthDate;
  const hasHouseholdMemberErrors =
    !!householdMemberErrors?.phoneNumber?.message ||
    !!householdMemberErrors?.firstName?.message ||
    !!householdMemberErrors?.lastName?.message ||
    !!householdMemberErrors?.eMail?.message ||
    !!householdMemberBirthDateErrors?.day?.message ||
    !!householdMemberBirthDateErrors?.month?.message ||
    !!householdMemberBirthDateErrors?.year?.message;

  useEffect(() => {
    if (!product) {
      previousCartRef.current = null;
      return;
    }
    const quantity = Number(numberOfMembers);
    if (!Number.isFinite(quantity) || quantity <= 0) return;

    const promotionData = getPromotionDataFromSessionStorage();
    const previousCart = previousCartRef.current;
    if (!previousCart) {
      trackAddToCart(product, 'Medlemskap', 'Innmelding', quantity, promotionData);
      previousCartRef.current = { product, quantity };
      return;
    }
    if (previousCart.product?.productNumber !== product.productNumber) {
      if (previousCart.product && previousCart.quantity > 0) {
        trackRemoveFromCart(previousCart.product, 'Medlemskap', 'Innmelding', previousCart.quantity, promotionData);
      }
      trackAddToCart(product, 'Medlemskap', 'Innmelding', quantity, promotionData);
      previousCartRef.current = { product, quantity };
      return;
    }
    if (quantity !== previousCart.quantity) {
      trackAddToCart(product, 'Medlemskap', 'Innmelding', quantity, promotionData);
      trackRemoveFromCart(product, 'Medlemskap', 'Innmelding', previousCart.quantity, promotionData);
      previousCartRef.current = { product, quantity };
    }
  }, [product, numberOfMembers, trackAddToCart, trackRemoveFromCart]);

  return {
    form,
    errors,
    availableAddonProducts,
    addonProductsDescription,
    supportsHouseholdMember,
    householdMemberPrice,
    isHouseholdMemberSelected,
    selectedHouseholdMember,
    members,
    products,
    mainMemberServerMessages,
    householdMemberErrors: hasHouseholdMemberErrors
      ? {
          phoneNumber: householdMemberErrors?.phoneNumber?.message,
          firstName: householdMemberErrors?.firstName?.message,
          lastName: householdMemberErrors?.lastName?.message,
          eMail: householdMemberErrors?.eMail?.message,
          birthDate: {
            day: householdMemberBirthDateErrors?.day?.message,
            month: householdMemberBirthDateErrors?.month?.message,
            year: householdMemberBirthDateErrors?.year?.message,
          },
        }
      : undefined,
    handleHouseholdMemberToggle,
    onSubmit: persistForm,
    onGoback: persistForm,
  };
};
